import {
  getMonth,
  getDate,
  getYear,
  format,
  startOfMonth,
  endOfMonth,
} from 'date-fns'
import parseSchedule from './parseSchedule'
import {
  formattedScheduleSeparator as FORMATTED_SCHEDULE_SEPARATORS,
  dateFormat as DATE_FORMAT,
  monthFormat as MONTH_FORMAT,
  yearFormat as YEAR_FORMAT,
} from './constants/format.json'
import * as Weekday from './constants/weekdays'

const constructDateSegmentFormat = (segmentFormat, year, month, date) => {
  return segmentFormat
    .replace('{month}', month)
    .replace('{date}', date)
    .replace('{year}', year)
}

const determineDateRangeElements = (start, end, refDate, isFirstElement) => {
  if (start === null) {
    return {
      start: {
        year: false,
        month: false,
        date: false,
      },
      end: {
        year: getYear(end) !== getYear(refDate),
        month: true,
        date: true,
      },
    }
  }

  return {
    start: {
      year:
        getYear(start) !== getYear(end) || getYear(start) !== getYear(refDate),
      month:
        isFirstElement ||
        getYear(start) !== getYear(refDate) ||
        getMonth(start) !== getMonth(end),
      date: !(
        getDate(start) === getDate(startOfMonth(start)) &&
        getDate(end) === getDate(endOfMonth(end))
      ),
    },
    end: {
      year:
        getYear(start) === getYear(refDate) && getYear(start) !== getYear(end),
      month:
        getYear(end) !== getYear(refDate) || getMonth(start) !== getMonth(end),
      date: !(
        getDate(start) === getDate(startOfMonth(start)) &&
        getDate(end) === getDate(endOfMonth(end))
      ),
    },
  }
}

const getFormatString = elements => {
  const { year: y, month: m, date: d } = elements

  if (!y && !m && d) {
    return '{date}'
  }
  if (!y && m && !d) {
    return '{month}'
  }
  if (!y && m && d) {
    return '{month} {date}'
  }
  if (y && !m && !d) {
    return '{yyyy}'
  }
  if (y && !m && d) {
    return '{date}, {year}'
  }
  if (y && m && !d) {
    return '{month} {year}'
  }
  if (y && m && d) {
    return '{month} {date}, {year}'
  }
  return ''
}

const getWeekdayNames = weekdays => {
  return Object.keys(Weekday).reduce(
    (weekdayNames, weekdayName) =>
      weekdays[weekdayName.toLowerCase()]
        ? [
            ...weekdayNames,
            `${weekdayName.charAt(0)}${weekdayName.slice(1).toLowerCase()}`,
          ]
        : weekdayNames,
    [],
  )
}

const reverseArray = items => {
  return items.reduce((reversedItems, item) => [item, ...reversedItems], [])
}

const formatEnumeration = strArray => {
  if (strArray.length < 2) {
    return strArray[0]
  }

  const [lastChunk, ...precedingChunks] = reverseArray(strArray)
  return [reverseArray(precedingChunks).join(', '), lastChunk].join(' and ')
}

const formatMonthSchedules = (schedules, refDate) => {
  return month => {
    const chunks = schedules[month].map((s, i) => {
      const isFirstElement = i === 0

      const { start, end } = determineDateRangeElements(
        s.start,
        s.end,
        refDate,
        isFirstElement,
      )
      const formattedStart =
        s.start !== null
          ? format(
              s.start,
              constructDateSegmentFormat(
                getFormatString(start),
                YEAR_FORMAT,
                MONTH_FORMAT,
                DATE_FORMAT,
              ),
            )
          : null
      const formattedEnd =
        s.end !== null
          ? format(
              s.end,
              constructDateSegmentFormat(
                getFormatString(end),
                YEAR_FORMAT,
                MONTH_FORMAT,
                DATE_FORMAT,
              ),
            )
          : null
      const weekdayNames = getWeekdayNames(s.weekdays)
      const allWeekdays =
        weekdayNames.length === 0 ||
        weekdayNames.length === Object.keys(Weekday).length

      if (s.start === null) {
        if (s.end === null) {
          return ''
        }
        return allWeekdays
          ? `until ${formattedEnd}`
          : `every ${formatEnumeration(weekdayNames)} until ${formattedEnd}`
      }

      if (
        getYear(s.start) === getYear(s.end) &&
        getMonth(s.start) === getMonth(s.end)
      ) {
        if (getDate(s.start) === getDate(s.end)) {
          return `${formattedStart}`
        }
        if (
          getDate(s.start) === getDate(startOfMonth(s.start)) &&
          getDate(s.end) === getDate(endOfMonth(s.end))
        ) {
          return allWeekdays
            ? `whole month of ${formattedStart}`
            : `all ${formatEnumeration(
                weekdayNames.map(name => `${name}s`),
              )} of ${formattedStart}`
        }
        if (isFirstElement && getDate(s.end) - getDate(s.start) === 1) {
          return `${formattedStart} and ${formattedEnd}`
        }

        if (isFirstElement && getDate(s.end) - getDate(s.start) === 2) {
          return `${formattedStart}, ${getDate(s.start) +
            1} and ${formattedEnd}`
        }
      }

      return allWeekdays
        ? `${formattedStart} to ${formattedEnd}`
        : `every ${formatEnumeration(
            weekdayNames,
          )} from ${formattedStart} to ${formattedEnd}`
    })

    if (chunks.length < 1) {
      return ''
    }

    if (chunks.length < 2) {
      return chunks[0]
    }

    return formatEnumeration(chunks)
  }
}

const formatGroupSchedules = (groupedSchedules, refDate) => {
  return Object.keys(groupedSchedules)
    .sort((a, b) => a - b)
    .map(formatMonthSchedules(groupedSchedules, refDate))
    .join(FORMATTED_SCHEDULE_SEPARATORS)
}

const groupSchedulesByMonth = scheduleGroup => {
  return scheduleGroup.reduce((monthSchedules, schedule) => {
    const scheduleMonth = getMonth(schedule.start)
    return {
      ...monthSchedules,
      [scheduleMonth]: [...(monthSchedules[scheduleMonth] || []), schedule],
    }
  }, {})
}

const formatDisplaySchedule = scheduleGroup => {
  if (!scheduleGroup) {
    return null
  }

  let theScheduleGroup = scheduleGroup
  if (typeof scheduleGroup === 'string') {
    theScheduleGroup = parseSchedule(scheduleGroup)
  }

  if (
    typeof theScheduleGroup === 'object' &&
    !(theScheduleGroup instanceof Array)
  ) {
    theScheduleGroup = [theScheduleGroup]
  }

  if (!(theScheduleGroup instanceof Array)) {
    return null
  }

  const groupedSchedules = groupSchedulesByMonth(theScheduleGroup)

  return formatGroupSchedules(groupedSchedules, new Date())
}

export default formatDisplaySchedule

// TODO merge dates
